import React, { useState, useRef, useCallback, useEffect } from "react";

import Button from "react-bootstrap/Button";
import { MdModeEdit } from "react-icons/md";
import { sendAuthenticatedAsyncRequest } from "services/AsyncRequestService";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import { Formik } from "formik";
import * as Yup from "yup";
import AdminPopUpDismissableModal from "components/adminPopupDismissableModal";
import { getErrorMessage } from "services/UtilityService";
import { showNotification } from "services/NotificationService";
import { formatDateWithTime } from 'services/MomentUtils';
import { twoDecimalPlaceRounder } from 'services/UtilityService';
import "assets/css/user.css";

const EditCycle = (props) => {
  const [showModal, setShowModal] = useState(!props.showButton);
  const modalRef = useRef(null);

  // Sync showModal state with props.showButton changes
  useEffect(() => {
    setShowModal(!props.showButton);
  }, [props.showButton]);

  const handleEditCycle = (values, setSubmitting, closeModalHandle) => {
    let valuesDto = {};
    valuesDto.description = values.description; 
    valuesDto.wfCycleId = values.wfCycleId;
    sendAuthenticatedAsyncRequest(
      `/swim/update-cycle`,
      "PUT",
      valuesDto,
      (r) => {
        setSubmitting(false);
        showNotification({
          title: "Action successful!",
          message: "Cycle edited successfully.",
          position: "tc",
          type: "success",
        });
        closeModalHandle();
        props.handler();
      },
      (r) => {
        setSubmitting(false);
        showNotification({
          title: "Error!",
          message:
            getErrorMessage(r.data) || getErrorMessage("UNABLE_TO_EDIT_CYCLE"),
          position: "tc",
          type: "error",
        });
      }
    );
  };

  const toggleModal = useCallback(() => {
    setShowModal(prevShowModal => !prevShowModal);
  }, []);

  const { cycle } = props;

  if (!showModal) {
    return (
      <OverlayTrigger overlay={<Tooltip>Edit Cycle</Tooltip>}>
        <span>
          <MdModeEdit
            size={20}
            className="user-action-button edit-user"
            onClick={toggleModal}
          />
        </span>
      </OverlayTrigger>
    );
  }

  const editCycleFormSchema = Yup.object().shape({
    description: Yup.string().max(100, "Maximum Length: 100"),
    dateScheduled: Yup.string().required("Date Scheduled is required"),
    status: Yup.string().required("Status is required"),
    projectedSavings: Yup.string().min(0.0, "Minimum Savings: 0.0"),
    totalSwitches: Yup.string().min(0, "Minimum Switches: 0"),
  });

  return (
    <>
      <OverlayTrigger overlay={<Tooltip>Edit Cycle</Tooltip>}>
        <span>
          <MdModeEdit
            className="user-action-button edit-user"
            size={20}
            onClick={toggleModal}
          />
        </span>
      </OverlayTrigger>
      <AdminPopUpDismissableModal
        ref={modalRef}
        openModalButtonText="Edit Cycle"
        openModalButtonStyle="info"
        openModalButtonBlock={true}
        openModalButtonDisabled={false}
        openModalBsuttonId="edit-cycle-btn-popup"
        preventCloseWhenClickedOutsideModal={true}
        initialStateOpen={true}
        hideFooter={true}
        onClose={toggleModal}
        title={"Edit Cycle Details"}
        showIcons={true}
      >
        <Formik
          initialValues={{ ...cycle }}
          onSubmit={(values, { setSubmitting }) => {
            handleEditCycle(
              values,
              setSubmitting,
              modalRef.current.handleClose
            );
          }}
          validationSchema={editCycleFormSchema}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            isSubmitting,
          }) => (
            <Form onSubmit={handleSubmit}>
              <Row>
                <Col>
                  <Form.Group>
                    <Form.Label>Description</Form.Label>
                    <Form.Control
                      type="text"
                      value={values.description}
                      onChange={handleChange}
                      name="description"
                      onBlur={handleBlur}
                    />
                    <Form.Text className="text-small text-danger">
                      {errors.description && touched.description && errors.description}
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col>
                  <Form.Group>
                    <Form.Label>Cycle Status</Form.Label>
                    <Form.Control
                      type="text"
                      value={values.status}
                      onChange={handleChange}
                      name="status"
                      onBlur={handleBlur}
                      readOnly
                    />
                    <Form.Text className="text-small text-danger">
                      {errors.status && touched.status && errors.status}
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group>
                    <Form.Label>Date Scheduled</Form.Label>
                    <Form.Control
                      type="text"
                      value={formatDateWithTime(values.dateScheduled)}
                      onChange={handleChange}
                      name="dateScheduled"
                      onBlur={handleBlur}
                      readOnly
                    />
                    <Form.Text className="text-small text-danger">
                      {errors.dateScheduled && touched.dateScheduled && errors.dateScheduled}
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col>
                  <Form.Group>
                    <Form.Label>Total Switches</Form.Label>
                    <Form.Control
                      type="text"
                      value={values.totalSwitches}
                      onChange={handleChange}
                      name="totalSwitches"
                      onBlur={handleBlur}
                      readOnly
                    />
                    <Form.Text className="text-small text-danger">
                      {errors.totalSwitches && touched.totalSwitches && errors.totalSwitches}
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group>
                    <Form.Label>Projected Savings</Form.Label>
                    <Form.Control
                      type="text"
                      value={twoDecimalPlaceRounder(values.projectedSavings)}
                      onChange={handleChange}
                      name="projectedSavings"
                      onBlur={handleBlur}
                      readOnly
                    />
                    <Form.Text className="text-small text-danger">
                      {errors.projectedSavings && touched.projectedSavings && errors.projectedSavings}
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
              <Row className="bottom-spacer top-spacer">
                <Col md={{ offset: "4", span: "4" }}>
                  <Button
                    variant="outline-dark"
                    className="btn-block"
                    onClick={() => modalRef.current.handleClose()}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                </Col>
                <Col md={4}>
                  <Button
                    type="submit"
                    variant="success"
                    className="btn-block"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Editing... " : "Edit"}
                  </Button>
                </Col>
              </Row>
            </Form>
          )}
        </Formik>
      </AdminPopUpDismissableModal>
    </>
  );
};

export default EditCycle;